import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation, Outlet } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import {
  LayoutDashboard, Users, FileText, Briefcase, UserCircle, LogOut, Menu, X, ChevronRight, Settings
} from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

const SIDEBAR_LINKS = [
  { icon: LayoutDashboard, label: "Dashboard", path: "/admin" },
  { icon: Settings, label: "Site Content", path: "/admin/content" },
  { icon: Users, label: "Leads", path: "/admin/leads" },
  { icon: FileText, label: "Blog Posts", path: "/admin/blogs" },
  { icon: Briefcase, label: "Case Studies", path: "/admin/case-studies" },
  { icon: UserCircle, label: "Team Members", path: "/admin/team" },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/admin/login");
  };

  return (
    <div data-testid="admin-layout" className="min-h-screen bg-gray-50 flex">
      {/* Sidebar */}
      <aside className={`fixed inset-y-0 left-0 z-40 w-64 bg-white border-r border-gray-200 transform transition-transform lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="h-16 flex items-center justify-between px-6 border-b border-gray-100">
          <Link to="/admin" className="flex items-center gap-2">
            <div className="w-7 h-7 bg-navy-900 rounded-sm flex items-center justify-center">
              <span className="text-white font-serif text-sm font-semibold">C</span>
            </div>
            <span className="font-serif text-base text-navy-900 font-semibold">Admin</span>
          </Link>
          <button onClick={() => setSidebarOpen(false)} className="lg:hidden text-gray-400">
            <X className="w-5 h-5" />
          </button>
        </div>
        <nav className="p-4 space-y-1">
          {SIDEBAR_LINKS.map((link) => {
            const isActive = location.pathname === link.path;
            return (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`admin-nav-${link.label.toLowerCase().replace(/\s/g, '-')}`}
                onClick={() => setSidebarOpen(false)}
                className={`flex items-center gap-3 px-4 py-2.5 rounded-sm text-sm font-sans transition-colors ${
                  isActive ? 'bg-navy-900 text-white' : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <link.icon className="w-4 h-4" strokeWidth={1.5} />
                {link.label}
              </Link>
            );
          })}
        </nav>
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-gray-100">
          <div className="flex items-center gap-3 px-4 py-2 mb-2">
            <div className="w-8 h-8 bg-gold-500 rounded-sm flex items-center justify-center">
              <span className="text-navy-900 font-serif text-sm font-semibold">{user?.name?.[0] || "A"}</span>
            </div>
            <div>
              <p className="text-sm font-sans font-medium text-navy-900">{user?.name || "Admin"}</p>
              <p className="text-xs font-sans text-gray-400 capitalize">{user?.role || "admin"}</p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            data-testid="admin-logout-button"
            className="flex items-center gap-2 px-4 py-2 text-sm font-sans text-gray-500 hover:text-red-600 transition-colors w-full"
          >
            <LogOut className="w-4 h-4" strokeWidth={1.5} /> Sign Out
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 lg:ml-64">
        <header className="h-16 bg-white border-b border-gray-200 flex items-center justify-between px-6">
          <button onClick={() => setSidebarOpen(true)} className="lg:hidden text-gray-600">
            <Menu className="w-5 h-5" />
          </button>
          <div className="hidden lg:flex items-center gap-2 text-sm font-sans text-gray-400">
            <Link to="/" className="hover:text-navy-900 transition-colors">Website</Link>
            <ChevronRight className="w-3 h-3" />
            <span className="text-navy-900 font-medium">Admin Panel</span>
          </div>
          <Link to="/" className="text-sm font-sans text-gray-500 hover:text-navy-900 transition-colors">
            View Website &rarr;
          </Link>
        </header>
        <main className="p-6 md:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

// Dashboard
export function AdminDashboard() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    axios.get(`${API}/api/dashboard/stats`)
      .then(r => setStats(r.data))
      .catch(() => {});
  }, []);

  const statCards = stats ? [
    { label: "Total Leads", value: stats.total_leads, accent: false },
    { label: "New Leads", value: stats.new_leads, accent: true },
    { label: "Blog Posts", value: stats.total_blogs, accent: false },
    { label: "Case Studies", value: stats.total_case_studies, accent: false },
    { label: "Team Members", value: stats.total_team_members, accent: false },
  ] : [];

  return (
    <div data-testid="admin-dashboard">
      <h1 className="font-serif text-2xl text-navy-900 mb-6">Dashboard</h1>
      {stats ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          {statCards.map((s) => (
            <div key={s.label} className={`p-6 border ${s.accent ? 'border-gold-500 bg-gold-500/5' : 'border-gray-200 bg-white'}`}>
              <p className="text-3xl font-serif text-navy-900">{s.value}</p>
              <p className="text-xs font-sans text-gray-500 uppercase tracking-[0.1em] mt-1">{s.label}</p>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-gray-400 font-sans text-sm">Loading stats...</p>
      )}
    </div>
  );
}
